/**
 * Clairfield brand style configuration — colors, typography, and prompt
 * rules extracted from the client's slide template (colorScheme
 * "Custom 435" + fontScheme "Custom 113", layout placeholder geometry,
 * via the OOXML).
 *
 * The master's first layout is "Cover". Unqualified body inserts use
 * "Title one Content" (the one-box body). Most sample pages use "Title
 * Only" (title, subtitle and source, no content box) with hand-built
 * panels. The logo, dotted title rule and slide number live on the master.
 */

import type { ThemeColors } from "../../types";

export const CLAIRFIELD_COLORS = {
  black: "000000", // dk1
  white: "FFFFFF", // lt1
  navy: "002C54", // dk2 / hlink
  paleBlue: "EDF1F4", // lt2
  lightBlue: "5F93B3", // accent1
  teal: "0D7680", // accent2
  green: "79B48E", // accent3
  amber: "F8B318", // accent4
  sand: "DFD4B8", // accent5
  cyan: "31ACBD", // accent6

  rowGrey: "F2F2F2",
  ruleGrey: "D9D9D9",
  up: "00B050",
  down: "C00000",
} as const;

export const CLAIRFIELD_THEME_COLORS: ThemeColors = {
  dark1: CLAIRFIELD_COLORS.black,
  dark2: CLAIRFIELD_COLORS.navy,
  light1: CLAIRFIELD_COLORS.white,
  light2: CLAIRFIELD_COLORS.paleBlue,
  accent1: CLAIRFIELD_COLORS.lightBlue,
  accent2: CLAIRFIELD_COLORS.teal,
  accent3: CLAIRFIELD_COLORS.green,
  accent4: CLAIRFIELD_COLORS.amber,
  accent5: CLAIRFIELD_COLORS.sand,
  accent6: CLAIRFIELD_COLORS.cyan,
  hyperlink: CLAIRFIELD_COLORS.navy,
  followedHyperlink: CLAIRFIELD_COLORS.black,
  majorFont: "Arial Nova",
  minorFont: "Arial Nova",
};

export const CLAIRFIELD_CHART_SERIES_PALETTE = [
  CLAIRFIELD_COLORS.navy,
  CLAIRFIELD_COLORS.lightBlue,
  CLAIRFIELD_COLORS.teal,
  CLAIRFIELD_COLORS.green,
  CLAIRFIELD_COLORS.amber,
  CLAIRFIELD_COLORS.cyan,
  CLAIRFIELD_COLORS.sand,
];

export const CLAIRFIELD_SLIDE_DIMENSIONS = { width: 960, height: 540 } as const;

export const CLAIRFIELD_POSITIONS = {
  title: { left: 20.5, top: 28.5, width: 771.7, height: 20.2 },
  subtitle: { left: 37.8, top: 65.8, width: 884.4, height: 17.0 },
  content: { left: 37.8, top: 99.9, width: 884.5, height: 402.6 },
  source: { left: 37.8, top: 518.3, width: 754.0, height: 8.7 },
  pageNumber: { left: 876.9, top: 515.0, width: 39.0, height: 14.5 },
} as const;

export const CLAIRFIELD_STYLE_PROMPT = `

## CLAIRFIELD BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Clairfield.pptx" is the Clairfield visual standard: a sell-side advisory pitch (market overview, growth drivers, company profile, case studies, equity story, valuation methods, football field, trading comparables, precedent transactions, DCF, investor types, process and why-Clairfield pages). Match the closest precedent's structure. Replace its sample content; never carry over its Finnish IT services facts or bracketed placeholders.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Clairfield uses \`"Arial Nova"\` for titles, subtitles, body, tables, charts and sources.
NEVER omit fontFamily when Clairfield style is active. If a shape already has the wrong font (including Calibri or Aptos), include fontFamily to correct it.

### Color palette (from the Clairfield master colorScheme "Custom 435")
Use ONLY these Clairfield brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${CLAIRFIELD_COLORS.black}
- Text2 / dark2: Navy #${CLAIRFIELD_COLORS.navy}
- Background1 / light1: White #${CLAIRFIELD_COLORS.white}
- Background2 / light2: Pale blue #${CLAIRFIELD_COLORS.paleBlue}
- Accent1: Light blue #${CLAIRFIELD_COLORS.lightBlue}
- Accent2: Teal #${CLAIRFIELD_COLORS.teal}
- Accent3: Green #${CLAIRFIELD_COLORS.green}
- Accent4: Amber #${CLAIRFIELD_COLORS.amber}
- Accent5: Sand #${CLAIRFIELD_COLORS.sand}
- Accent6: Cyan #${CLAIRFIELD_COLORS.cyan}

**Usage:**
- **Primary brand / titles / table headers**: Navy #${CLAIRFIELD_COLORS.navy}
- **Subtitles, source lines, secondary heading bars**: Light blue #${CLAIRFIELD_COLORS.lightBlue}
- **Supporting accents**: Teal #${CLAIRFIELD_COLORS.teal}, green #${CLAIRFIELD_COLORS.green}, cyan #${CLAIRFIELD_COLORS.cyan}
- **Highlight**: Amber #${CLAIRFIELD_COLORS.amber} or sand #${CLAIRFIELD_COLORS.sand} — highlights, not default body colour
- **Body text**: Black #${CLAIRFIELD_COLORS.black}
- **Summary rows / rules**: Grey #${CLAIRFIELD_COLORS.rowGrey} and #${CLAIRFIELD_COLORS.ruleGrey}
- **Up / down**: Green #${CLAIRFIELD_COLORS.up} / red #${CLAIRFIELD_COLORS.down}
- **Chart series palette** (in order): ${CLAIRFIELD_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${CLAIRFIELD_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from "Title one Content")
Slide dimensions: 960 × 540 pt (16:9).

**Placeholder positions:**
- **Title**: left=${CLAIRFIELD_POSITIONS.title.left}, top=${CLAIRFIELD_POSITIONS.title.top}, width=${CLAIRFIELD_POSITIONS.title.width}, height=${CLAIRFIELD_POSITIONS.title.height} — Arial Nova 20pt bold, navy. The master adds a small navy square bullet before the title; do not draw your own.
- **Subtitle** (body idx 11): left=${CLAIRFIELD_POSITIONS.subtitle.left}, top=${CLAIRFIELD_POSITIONS.subtitle.top}, width=${CLAIRFIELD_POSITIONS.subtitle.width}, height=${CLAIRFIELD_POSITIONS.subtitle.height} — Arial Nova 14pt bold, light blue. Use it for the page's takeaway.
- **Content** (idx 1): left=${CLAIRFIELD_POSITIONS.content.left}, top=${CLAIRFIELD_POSITIONS.content.top}, width=${CLAIRFIELD_POSITIONS.content.width}, height=${CLAIRFIELD_POSITIONS.content.height} — Arial Nova 10.5pt, black, justified. Level-2 bullets are navy Wingdings squares.
- **Source** (body idx 12): left=${CLAIRFIELD_POSITIONS.source.left}, top=${CLAIRFIELD_POSITIONS.source.top}, width=${CLAIRFIELD_POSITIONS.source.width}, height=${CLAIRFIELD_POSITIONS.source.height} — Arial Nova 8pt, light blue, starting "Source:".
- **Slide number**: master text box at left=${CLAIRFIELD_POSITIONS.pageNumber.left}, top=${CLAIRFIELD_POSITIONS.pageNumber.top}. The .clairfield logo (top-right) and the dotted rule under the title band are master furniture; never add them yourself.

**Layout choice:**
- Default body: "Title one Content".
- "Title Only" is right for hand-built panel, table and chart pages; keep the subtitle and source placeholders.
- Do not default to "Cover" (the master's first layout), "Cover no photo", "1_Cover" or "Blank slide".
- Two, three, 2x2, 2x3, 1/3, 2/3, image, highlight, tombstone, team, agenda, table-of-contents, chapter-divider, contact and back-cover layouts are specialist. Clone them only for the same job. Do not rotate through the rest of the layout catalog.

### Page pattern (Clairfield style)
- Titles are often a question ("What valuation methods to use?"); the subtitle answers it with the supporting number ("List of comparables points to a median EBITDA multiple of 7.3x"). Descriptive titles are fine when a question would be forced.
- Panels: a navy or light-blue heading bar with white bold Arial Nova text, often led by a numbered badge (1, 2, 3...), then 10.5pt black body copy.

### Charts (Clairfield style)
- Font: Arial Nova, minimum 8pt
- **Primary data color**: navy (#${CLAIRFIELD_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Clairfield style)
- Header row: navy (#${CLAIRFIELD_COLORS.navy}) fill with white bold text; grouped metric headings (e.g. EV/Sales, EV/EBITDA) span their period columns
- Body: white fill, black Arial Nova text, thin grey (#${CLAIRFIELD_COLORS.ruleGrey}) row rules
- Median / average rows at the bottom: bold on grey (#${CLAIRFIELD_COLORS.rowGrey} / #${CLAIRFIELD_COLORS.ruleGrey})
- Label periods with A / E ("FY25A", "FY26E") and put units in the header ("€m", "x")

### Shapes & layout (Clairfield style)
- Separate content with sharp rectangular blocks — not rounded shapes
- Keep content above the source line and clear of the slide number
- Prefer "Title one Content" for ordinary body slides

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). The content box is wide; long sentences still overflow.
`;
